import React, { useEffect, useState } from "react";
import { FaTimes, FaPen, FaBuilding, FaCube, FaClock, FaSearch } from "react-icons/fa";
import { CustomDropdown } from "../../atoms";
import useAuth from "../../../Hooks/useAuth";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import { useUpdateDeviceConsumentLogMutation } from "../../../store/storageRoles/devicesSharedApi";
import { useLazySearchFirmsForDropdownQuery } from "../../../store/storageRoles/firmApi";

/**
 * ConsumentEditDrawer — Cihaz raporundaki tekil (saatlik) tüketim kaydının
 * DURUM (deviceStatusName) ve FİRMA'sını düzeltir. SADECE admin açabilir
 * (server tarafı da restrictTo("admin") ile korur). Tüm tüketim raporları
 * ve dashboard, kaydın kendi firm/deviceStatusName alanından toplandığı
 * için düzeltme geriye dönük olarak otomatik yansır; kota bakiyeleri ve
 * faturalar etkilenmez.
 */

const STATUS_OPTIONS = ["Depo", "Firma", "Servis", "Plasiyer", "Üretim"].map(
  (s) => ({ id: s, label: s, value: s })
);

const ConsumentEditDrawer = ({ open, onClose, log: logProp, onSuccess }) => {
  // Giriş/çıkış kayması + kapanışta kaydın dondurulması.
  const { mounted, entered, data: log } = useDrawerTransition(open, logProp);
  const { auth } = useAuth();
  const [statusName, setStatusName] = useState("");
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [firmSearch, setFirmSearch] = useState("");

  const [updateLog, { isLoading, isSuccess, isError, error, reset }] =
    useUpdateDeviceConsumentLogMutation();
  const [searchFirms, { data: firmSearchData, isFetching: firmsLoading }] =
    useLazySearchFirmsForDropdownQuery();
  const firmResults = firmSearchData?.data?.firms || [];

  useEffect(() => {
    if (open && log) {
      setStatusName(log.deviceStatusName || "");
      setSelectedFirm(null);
      setFirmSearch("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, log?.logId]);

  // Firma araması — 2+ karakterde debounce ile sorgula
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (firmSearch.trim().length >= 2) {
        searchFirms({ token: auth.accessToken, search: firmSearch.trim() });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmSearch, open]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const statusChanged = statusName && statusName !== log?.deviceStatusName;
  const firmChanged = !!selectedFirm?._id && selectedFirm._id !== log?.firm;
  const canSave = (statusChanged || firmChanged) && !isLoading;

  const handleSave = () => {
    if (!canSave || !log?.logId) return;
    updateLog({
      logId: log.logId,
      ...(firmChanged ? { firmId: selectedFirm._id } : {}),
      ...(statusChanged ? { deviceStatusName: statusName } : {}),
      token: auth.accessToken,
    });
  };

  return (
    <>
      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[440px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9997] flex flex-col bg-gray-50`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 shrink-0">
              <FaPen className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-800 truncate">Tüketim Kaydını Düzenle</h3>
              <p className="text-sm text-gray-500 truncate">Durum ve firma düzeltmesi (admin)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        {log && (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
            {/* Kayıt özeti */}
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
                  <FaCube className="w-3.5 h-3.5 text-indigo-400" />
                  {log.productName || "-"}
                </span>
                <span className="text-base font-extrabold text-indigo-600 tabular-nums">
                  {Number(log.consument ?? 0).toLocaleString("tr-TR")} kota
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <FaClock className="w-3 h-3 text-gray-400" />
                  {log.createdInfo
                    ? new Date(log.createdInfo).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })
                    : "-"}
                </span>
                <span>
                  Mevcut durum: <span className="font-semibold text-gray-700">{log.deviceStatusName || "-"}</span>
                </span>
                {log.firmName && (
                  <span className="inline-flex items-center gap-1">
                    <FaBuilding className="w-3 h-3 text-gray-400" />
                    <span className="font-semibold text-gray-700">{log.firmName}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Durum */}
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 font-semibold tracking-wider mb-2">DURUM</p>
              <CustomDropdown
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === statusName) || null}
                onChange={(opt) => setStatusName(opt?.value ?? "")}
                placeholder="Durum seçin"
                showSearch={false}
              />
            </div>

            {/* Firma — uzak arama (2+ karakter) */}
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 font-semibold tracking-wider mb-2">FİRMA</p>
              {selectedFirm ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-indigo-800 min-w-0">
                    <FaBuilding className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{selectedFirm.firmName}</span>
                  </span>
                  <button
                    onClick={() => setSelectedFirm(null)}
                    className="p-1 rounded-full text-indigo-400 hover:bg-indigo-100 shrink-0"
                    title="Seçimi kaldır"
                  >
                    <FaTimes className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
                    <input
                      type="text"
                      value={firmSearch}
                      onChange={(e) => setFirmSearch(e.target.value)}
                      placeholder={log.firmName ? `Mevcut: ${log.firmName} — değiştirmek için ara` : "Firma ara (en az 2 harf)"}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                    />
                  </div>
                  {firmSearch.trim().length >= 2 && (
                    <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
                      {firmsLoading ? (
                        <p className="px-3 py-2.5 text-xs text-gray-400">Aranıyor...</p>
                      ) : firmResults.length === 0 ? (
                        <p className="px-3 py-2.5 text-xs text-gray-400">Sonuç bulunamadı</p>
                      ) : (
                        firmResults.map((f) => (
                          <button
                            key={f._id}
                            onClick={() => setSelectedFirm(f)}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50"
                          >
                            <FaBuilding className="w-3 h-3 text-indigo-400 shrink-0" />
                            <span className="truncate">{f.firmName}</span>
                            {f.bayserNo && <span className="text-[11px] text-gray-400 shrink-0">#{f.bayserNo}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
              <p className="mt-1.5 text-[11px] text-gray-400">
                Boş bırakılırsa firma değişmez; yalnızca seçim yapılırsa güncellenir.
              </p>
            </div>

            {/* Bilgi notu */}
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-3.5 py-3">
              <p className="text-xs text-blue-800 leading-snug">
                Düzeltme tüm tüketim raporlarına ve dashboard kırılımlarına
                geriye dönük yansır. Kota bakiyeleri ve faturalar değişmez.
              </p>
            </div>

            {isError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3">
                <p className="text-xs text-red-700">
                  {error?.data?.message || "Güncelleme sırasında hata oluştu."}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="shrink-0 px-4 py-3 bg-white border-t border-gray-200 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Backdrop */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9996] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
        />
      )}
    </>
  );
};

export default ConsumentEditDrawer;
